import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IEmployeeShort } from "toolkit/employees/types";
import { getCommission, getRoles } from "./stepOne.thunks";
import { Commission, CommissionMember, CommissionRole } from "./stepOne.types";

interface StepOneState {
	currentCommission: Omit<Commission, "listOfCommissionMembers"> | null;
	alreadyAddedEmployees: CommissionMember[];
	roles: CommissionRole[];
	loading: boolean;
	error: string;
}

const initialState: StepOneState = {
	currentCommission: null,
	alreadyAddedEmployees: [],
	roles: [],
	loading: false,
	error: "",
};

const stepOneSlice = createSlice({
	name: "stepOneSlice",
	initialState,
	reducers: {
		clearCommission(state) {
			state.currentCommission = null;
		},
		setCommission(
			state,
			action: PayloadAction<{
				commission: Omit<Commission, "listOfCommissionMembers" | "id"> | null;
				commissionId: number;
			}>,
		) {
			if (action.payload.commission) {
				state.currentCommission = {
					...action.payload.commission,
					id: action.payload.commissionId,
				};
			} else {
				state.currentCommission = null;
			}
		},

		addEmployee(
			state,
			action: PayloadAction<{
				employee: IEmployeeShort;
				commissionId: string | undefined;
			}>,
		) {
			const candidate = state.alreadyAddedEmployees.find(
				(employee) => employee.employeeTableEmployee.id === action.payload.employee.id,
			);

			if (!candidate) {
				state.alreadyAddedEmployees.push({
					id: 0,
					commissionId: Number(action.payload.commissionId),
					commissionRoleId: {
						id: 0,
						name: "",
					},
					employeeTableEmployee: {
						id: action.payload.employee.id,
						fullName: action.payload.employee.fullName,
					},
				});
			}
		},

		addEmployees(state, action: PayloadAction<CommissionMember[]>) {
			state.alreadyAddedEmployees = action.payload;
		},

		editEmployeeRole(
			state,
			action: PayloadAction<{
				employee: CommissionMember;
				role: CommissionRole;
			}>,
		) {
			const employeesIds = state.alreadyAddedEmployees.map((employee) => employee.employeeTableEmployee.id);

			const candidateIndex = employeesIds.indexOf(action.payload.employee.employeeTableEmployee.id);
			if (candidateIndex !== -1) {
				state.alreadyAddedEmployees[candidateIndex] = {
					...action.payload.employee,
					commissionRoleId: { ...action.payload.role },
				};
			}
		},

		deleteEmployee(state, action: PayloadAction<number>) {
			const newData = state.alreadyAddedEmployees.filter(
				(employee) => employee.employeeTableEmployee.id !== action.payload,
			);

			state.alreadyAddedEmployees = newData;
		},

		clearEmployees(state) {
			state.alreadyAddedEmployees = [];
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getCommission.pending, (state) => {
			state.loading = true;
		});

		builder.addCase(getCommission.fulfilled, (state, action: PayloadAction<any>) => {
			state.currentCommission = action.payload;
			console.log(action.payload);
			state.alreadyAddedEmployees = action.payload.listOfCommissionMembers.map((_: any) => ({
				..._,
				employeeTableEmployee: {
					id: _.employeeTableEmployee.id,
					fullName: `${_.employeeTableEmployee.lastName} ${_.employeeTableEmployee.firstName} ${_.employeeTableEmployee.middleName}`,
				},
			}));
			state.loading = false;
			state.error = "";
		});

		builder.addCase(getCommission.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload as any;
		});

		builder.addCase(getRoles.fulfilled, (state, action: PayloadAction<CommissionRole[]>) => {
			state.roles = action.payload;
		});
	},
});

export const {
	setCommission,
	addEmployee,
	addEmployees,
	editEmployeeRole,
	clearCommission,
	deleteEmployee,
	clearEmployees,
} = stepOneSlice.actions;
export default stepOneSlice.reducer;
